import { forwardRef, useMemo, useRef, useState, useLayoutEffect } from "react";
import { fmtTime, fmtDur, todayNZ, isoToNZInput } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { GanttChartSquare, Radio, MessageSquare, Coffee, Utensils, AlertTriangle, LogOut, Undo2, Pause } from "lucide-react";

const HOUR_PX = 84;
const LANE_H = 56;
const LABEL_W = 180;

export const TIMELINE_KINDS = {
  clean: { bg: "bg-indigo-500", dot: "bg-indigo-500", label: "Room clean" },
  strip: { bg: "bg-rose-500", dot: "bg-rose-500", label: "Strip" },
  task: { bg: "bg-sky-500", dot: "bg-sky-500", label: "Scheduled task" },
  extra: { bg: "bg-violet-500", dot: "bg-violet-500", label: "Extra task" },
  refill: { bg: "bg-amber-500", dot: "bg-amber-500", label: "Refill trolley" },
  assist: { bg: "bg-teal-500", dot: "bg-teal-500", label: "Assisting" },
};

// ISO -> minutes-of-day (NZ wall clock), relative to `base` (YYYY-MM-DD) midnight.
export function nzMin(iso, base) {
  if (!iso) return null;
  const s = isoToNZInput(iso);
  const [d, t] = s.split("T");
  const [h, m] = t.split(":").map(Number);
  const dayDiff = Math.round(
    (Date.parse(`${d}T00:00:00Z`) - Date.parse(`${base}T00:00:00Z`)) / 86400000
  );
  return dayDiff * 1440 + h * 60 + m;
}

const TimelineTrack = forwardRef(function TimelineTrack(
  { lanes = [], date, gapThreshold = 900, onItemClick, onGapClick, onBreakClick, onClockOut, onClockOutUndo, clockOutPending = false, showLaneLabels = true, rangeStart = 510, rangeEnd = 870, finishMin = null, originalFinishMin = null, finishShade = "none" },
  ref
) {
  const isToday = date === todayNZ();
  const nowMin = isToday ? nzMin(new Date().toISOString(), date) : null;
  const showFinish = isToday && finishMin != null;
  const showOriginal = originalFinishMin != null;

  const { domStart, domEnd, hours, hasItems } = useMemo(() => {
    const mins = [];
    lanes.forEach((l) =>
      l.items.forEach((it) => {
        mins.push(nzMin(it.start, date));
        mins.push(nzMin(it.eff_end, date));
      })
    );
    const hasItems = mins.length > 0;
    // The chosen window sets the scale. Start stays fixed; the end auto-extends to the
    // nearest half hour if anyone worked past it, so late work is never clipped.
    let a = rangeStart;
    let b = rangeEnd;
    if (b <= a) b = a + 60;
    // Keep the planned (original) finish marker in view even if it lands before the window.
    if (showOriginal && originalFinishMin < a) a = Math.floor(originalFinishMin / 30) * 30;
    const dataMax = mins.length ? Math.max(...mins) : null;
    if (dataMax != null && dataMax > b) b = Math.ceil(dataMax / 30) * 30;
    // Keep the projected finish marker inside the visible window.
    if (showFinish && finishMin > b) b = Math.ceil(finishMin / 30) * 30;
    if (showOriginal && originalFinishMin > b) b = Math.ceil(originalFinishMin / 30) * 30;
    const hrs = [];
    for (let h = Math.ceil(a / 60) * 60; h <= b; h += 60) hrs.push(h);
    return { domStart: a, domEnd: b, hours: hrs, hasItems };
  }, [lanes, date, rangeStart, rangeEnd, showFinish, finishMin, showOriginal, originalFinishMin]);

  // Measure the available width so the track can stretch to fill the screen (wider bars
  // => more room labels visible). Falls back to horizontal scroll for very long spans.
  const wrapRef = useRef(null);
  const [availW, setAvailW] = useState(0);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setAvailW(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!hasItems && !showOriginal) {
    return (
      <Card ref={wrapRef} className="flex flex-col items-center gap-3 rounded-md border border-dashed py-16 text-center" data-testid="timeline-empty">
        <GanttChartSquare className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No cleaning activity recorded yet.</p>
      </Card>
    );
  }

  const span = domEnd - domStart;
  const labelW = showLaneLabels ? LABEL_W : 0;
  const naturalTrackW = (span / 60) * HOUR_PX;
  const fillTrackW = Math.max(0, availW - labelW);
  // Stretch to fill the screen when there's room; otherwise keep natural size + scroll.
  const trackW = Math.max(naturalTrackW, fillTrackW);
  const pxPerMin = span > 0 ? trackW / span : 0;
  const xOf = (min) => (min - domStart) * pxPerMin;
  // Markers are clamped to the visible range so a plan/est that falls outside the chosen
  // hours still shows (pinned at the edge with an arrow) instead of silently disappearing.
  const clampX = (min) => Math.max(0, Math.min(trackW, xOf(min)));
  const finishInView = showFinish;
  const finishClamped = showFinish && (finishMin < domStart || finishMin > domEnd);
  const finishX = showFinish ? clampX(finishMin) : 0;
  const fm = (((finishMin % 1440) + 1440) % 1440);
  const finishHm = `${String(Math.floor(fm / 60)).padStart(2, "0")}:${String(fm % 60).padStart(2, "0")}`;
  const originalInView = showOriginal;
  const originalClamped = showOriginal && (originalFinishMin < domStart || originalFinishMin > domEnd);
  const originalX = showOriginal ? clampX(originalFinishMin) : 0;
  const ofm = (((originalFinishMin % 1440) + 1440) % 1440);
  const originalHm = `${String(Math.floor(ofm / 60)).padStart(2, "0")}:${String(ofm % 60).padStart(2, "0")}`;
  // Shade the band between the planned (grey) and live (orange) finish: green = ahead of
  // the original plan, red = behind. Only meaningful when both markers are shown.
  const bandLeft = showOriginal && showFinish ? Math.min(originalX, finishX) : 0;
  const bandWidth = showOriginal && showFinish ? Math.abs(finishX - originalX) : 0;
  const bandClass = finishShade === "ahead" ? "bg-emerald-400/20" : finishShade === "behind" ? "bg-red-400/20" : "";

  return (
    <div ref={wrapRef} className="w-full">
    <Card className="overflow-hidden rounded-md border p-0">
      <div className="overflow-x-auto">
        <div ref={ref} className="bg-card" style={{ minWidth: labelW + trackW }}>
          {/* axis header */}
          <div className="flex border-b border-border bg-muted/40">
            {showLaneLabels && (
              <div style={{ width: labelW }} className="sticky left-0 z-20 flex items-center bg-muted/40 px-4 py-2 text-xs font-semibold text-muted-foreground">
                Cleaner
              </div>
            )}
            <div className="relative" style={{ width: trackW, height: 30 }}>
              {hours.map((h) => (
                <div key={h} className="absolute top-0 h-full border-l border-border/60 pl-1 text-[11px] text-muted-foreground" style={{ left: xOf(h) }}>
                  {String(Math.round(h / 60) % 24).padStart(2, "0")}:00
                </div>
              ))}
              {finishInView && (
                <div className="absolute top-0 z-30 -translate-x-1/2 whitespace-nowrap rounded-b bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow" style={{ left: finishX }} data-testid="timeline-finish-label">
                  {finishMin < domStart && finishClamped ? "◀ " : ""}Est. finish {finishHm}{finishMin > domEnd && finishClamped ? " ▶" : ""}
                </div>
              )}
              {originalInView && (
                <div className={`absolute z-20 -translate-x-1/2 whitespace-nowrap rounded-b bg-slate-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow ${finishClamped && originalClamped && Math.abs(finishX - originalX) < 2 ? "top-[15px]" : "top-0"}`} style={{ left: originalX }} data-testid="timeline-original-finish-label">
                  {originalFinishMin < domStart && originalClamped ? "◀ " : ""}Planned {originalHm}{originalFinishMin > domEnd && originalClamped ? " ▶" : ""}
                </div>
              )}
            </div>
          </div>

          {/* lanes */}
          {lanes.map((lane) => (
            <div key={lane.cleaner_id} className="flex border-b border-border last:border-b-0" data-testid={`timeline-lane-${lane.cleaner_id}`}>
              {showLaneLabels && (
                <div style={{ width: labelW }} className="sticky left-0 z-20 flex flex-col justify-center gap-0.5 bg-card px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    <div className="truncate text-sm font-semibold">{lane.cleaner_name}</div>
                    {lane.items.length > 0 && (
                      lane.clock_out_at ? (
                        <button
                          type="button"
                          onClick={onClockOutUndo ? () => onClockOutUndo(lane) : undefined}
                          disabled={clockOutPending}
                          title="Undo clock-out"
                          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 transition-colors hover:bg-slate-200 disabled:opacity-50"
                          data-testid={`timeline-clockout-undo-${lane.cleaner_id}`}
                        >
                          <Undo2 className="h-3 w-3" /> Out {fmtTime(lane.clock_out_at)}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={onClockOut && lane.last_completed_end ? () => onClockOut(lane) : undefined}
                          disabled={clockOutPending || !lane.last_completed_end}
                          title={lane.last_completed_end ? "Clock out to end of last job" : "No completed jobs yet"}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 transition-colors hover:bg-amber-200 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid={`timeline-clockout-${lane.cleaner_id}`}
                        >
                          <LogOut className="h-3 w-3" /> Clock out
                        </button>
                      )
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {lane.items.length ? `${fmtDur(lane.worked_seconds)} worked` : "No activity"}
                  </div>
                </div>
              )}
              <div className="relative overflow-hidden bg-card" style={{ width: trackW, height: LANE_H }}>
                {hours.map((h) => (
                  <div key={h} className="absolute top-0 bottom-0 border-l border-border/40" style={{ left: xOf(h) }} />
                ))}
                {isToday && nowMin >= domStart && nowMin <= domEnd && (
                  <div className="absolute top-0 bottom-0 z-10 w-px bg-emerald-500" style={{ left: xOf(nowMin) }} data-testid="timeline-now-line">
                    <div className="absolute -top-0.5 -left-[3px] h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </div>
                )}
                {finishInView && bandWidth > 0 && bandClass && (
                  <div className={`absolute top-0 bottom-0 z-0 ${bandClass}`} style={{ left: bandLeft, width: bandWidth }} data-testid="timeline-finish-band" />
                )}
                {originalInView && (
                  <div className="absolute top-0 bottom-0 z-10 border-l-2 border-dashed border-slate-400/80" style={{ left: originalX }} data-testid="timeline-original-finish-line" />
                )}
                {finishInView && (
                  <div className="absolute top-0 bottom-0 z-10 border-l-2 border-dashed border-orange-500/80" style={{ left: finishX }} data-testid="timeline-finish-line" />
                )}
                {/* idle gaps */}
                {lane.gaps
                  .filter((g) => g.seconds >= gapThreshold)
                  .map((g, i) => {
                    const l = xOf(nzMin(g.start, date));
                    const w = Math.max(6, xOf(nzMin(g.end, date)) - l);
                    const explained = !!g.reason;
                    const cls = explained
                      ? "bg-slate-200 text-slate-700 border border-slate-300"
                      : "bg-amber-200/60 text-amber-800 [background-image:repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(180,83,9,.35)_4px,rgba(180,83,9,.35)_7px)]";
                    return (
                      <button
                        key={`gap-${i}`}
                        onClick={onGapClick ? () => onGapClick(g, lane) : undefined}
                        data-testid={`timeline-gap-${lane.cleaner_id}-${i}`}
                        disabled={!onGapClick}
                        className={`absolute top-1/2 flex -translate-y-1/2 items-center gap-1 overflow-hidden rounded-sm px-1 ${cls} ${onGapClick ? "cursor-pointer hover:brightness-95" : ""}`}
                        style={{ left: l, width: w, height: 18 }}
                        title={explained ? `${g.reason}${g.note ? " — " + g.note : ""} (${fmtDur(g.seconds)})` : `Idle ${fmtDur(g.seconds)} — tap to add a reason`}
                      >
                        {w > 60 ? (
                          <span className="truncate text-[10px] font-semibold">
                            {explained ? g.reason : fmtDur(g.seconds)}
                          </span>
                        ) : (
                          onGapClick && !explained && <MessageSquare className="h-3 w-3 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                {/* task bars — split at every break so the pause is a visible gap, not an overlay */}
                {lane.items.map((it, i) => {
                  const s = nzMin(it.start, date);
                  const e = nzMin(it.eff_end, date);
                  const kd = TIMELINE_KINDS[it.kind] || TIMELINE_KINDS.clean;
                  const brks = (it.breaks || []).filter((b) => b.start).map((b) => ({
                    bs: Math.max(s, nzMin(b.start, date)), be: Math.min(e, b.end ? nzMin(b.end, date) : e),
                  })).sort((a, b) => a.bs - b.bs);
                  const segs = [];
                  let cur = s;
                  for (const b of brks) {
                    if (b.bs > cur) segs.push([cur, b.bs]);
                    cur = Math.max(cur, b.be);
                  }
                  if (e > cur || segs.length === 0) segs.push([cur, Math.max(e, cur)]);
                  const widest = segs.reduce((m, sg, j) => (sg[1] - sg[0] > segs[m][1] - segs[m][0] ? j : m), 0);
                  const title = `${it.label} · ${fmtTime(it.start)}–${it.running ? "now" : fmtTime(it.end)} · ${fmtDur(it.actual_seconds)}${brks.length ? ` (${brks.length} break${brks.length > 1 ? "s" : ""} excluded)` : ""}${it.auto_closed ? " · Auto-closed by KlenaPro" : ""}`;
                  return segs.map(([ss, se], j) => {
                    const left = xOf(ss);
                    const w = Math.max(6, xOf(se) - left);
                    const first = j === 0, last = j === segs.length - 1;
                    const radius = segs.length === 1 ? "rounded-md" : first ? "rounded-l-md" : last ? "rounded-r-md" : "";
                    return (
                      <button
                        key={`${it.clean_id}-${it.role}-${i}-${j}`}
                        onClick={onItemClick ? () => onItemClick(it, lane) : undefined}
                        data-testid={j === 0 ? `timeline-item-${it.clean_id}-${it.role}` : `timeline-item-${it.clean_id}-${it.role}-seg-${j}`}
                        data-segment={`${j + 1}/${segs.length}`}
                        className={`absolute top-1/2 flex -translate-y-1/2 items-center overflow-hidden ${radius} ${kd.bg} px-1.5 text-left text-white shadow-sm transition-transform hover:scale-[1.02] hover:shadow-md ${last && it.running ? "ring-2 ring-emerald-400 ring-offset-1" : last && it.auto_closed ? "ring-2 ring-sky-300 ring-offset-1" : ""}`}
                        style={{ left, width: w, height: 30 }}
                        title={title}
                      >
                        {j === widest && w > 26 && (
                          <span className="relative z-10 truncate text-[11px] font-semibold leading-tight">
                            {it.label}
                          </span>
                        )}
                      </button>
                    );
                  });
                })}
                {/* lunch/break bubbles sit in the gap between bar segments — clickable for the manager to correct times */}
                {(() => {
                  const bubbles = [];
                  lane.items.forEach((it) => (it.breaks || []).forEach((b) => {
                    if (!b.start) return;
                    bubbles.push({ ...b, source: "task", clean_id: it.clean_id, role: it.role, session_started_at: it.start, itemEnd: it.eff_end, item: it });
                  }));
                  (lane.shift_breaks || []).forEach((b) => {
                    if (!b.start || (b.type !== "lunch" && b.type !== "break")) return;
                    bubbles.push({ ...b, source: "shift" });
                  });
                  return bubbles.map((b, bi) => {
                    const bs = nzMin(b.start, date);
                    if (bs < domStart || bs > domEnd) return null;
                    const be = b.end ? nzMin(b.end, date) : (b.itemEnd ? nzMin(b.itemEnd, date) : bs + 5);
                    const bl = xOf(bs);
                    const bw = Math.max(22, xOf(be) - bl);
                    const unfinished = !b.end;
                    const isLunch = b.type === "lunch";
                    const isPause = b.type === "pause";
                    const Icon = isLunch ? Utensils : isPause ? Pause : Coffee;
                    const mins = Math.max(0, Math.round(be - bs));
                    const base = isLunch ? "bg-orange-500" : isPause ? "bg-slate-500" : "bg-amber-500";
                    const click = isPause ? (onItemClick && b.item ? () => onItemClick(b.item, lane) : undefined) : (onBreakClick ? () => onBreakClick(b, lane) : undefined);
                    return (
                      <button
                        key={`brk-${b.source}-${b.clean_id || "s"}-${b.index}-${bi}`}
                        onClick={click}
                        disabled={!click}
                        data-testid={`timeline-break-${lane.cleaner_id}-${b.source}-${b.index}`}
                        className={`absolute top-1/2 z-20 flex -translate-y-1/2 items-center gap-1 overflow-hidden rounded-full border-2 border-white px-1.5 text-white shadow-md ${base} ${unfinished ? "ring-2 ring-red-500 ring-offset-1 animate-pulse" : ""} ${click ? "cursor-pointer hover:brightness-110" : ""}`}
                        style={{ left: bl, width: bw, height: 22 }}
                        title={`${isLunch ? "Lunch" : isPause ? "Paused" : "Break"} · ${fmtTime(b.start)}–${unfinished ? "not ended" : fmtTime(b.end)}${unfinished ? " · tap to fix" : ` · ${mins} min`}${b.source === "task" ? " · not counted in the room time" : ""}`}
                      >
                        {unfinished ? <AlertTriangle className="h-3 w-3 shrink-0" /> : <Icon className="h-3 w-3 shrink-0" />}
                        {bw > 44 && (
                          <span className="truncate text-[10px] font-bold leading-none">
                            {unfinished ? "?" : `${mins}m`}
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
    </div>
  );
});

export default TimelineTrack;
